import { AlertCircle, CheckCircle2, FileText, UploadCloud, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import type { AllowedFileType, FileUploadOptions, UploadedFile } from '@/types/api/file'
import { cn } from '@/utils/cn'
import { getErrorMessage } from '@/utils/error-parser'
import {
  describeSavings,
  prepareFileForUpload,
  validatePrepared,
  type PreparedFile,
} from '@/utils/file-compression'
import {
  buildAcceptAttribute,
  formatBytes,
  type FileRules,
} from '@/utils/file-utils'

export interface FileUploadModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Uploads one file; must honour the abort signal so Cancel stops the request. The file may be a
   * smaller version of what was chosen (see {@link prepareFileForUpload}); when its bytes are
   * compressed the options say so and must reach the server.
   */
  upload: (file: File, options: UploadOptions) => Promise<UploadedFile | void>
  /** Called with the files that were uploaded (also when only some of several succeeded). */
  onUploaded?: (files: UploadedFile[]) => void
  title?: string
  description?: string
  /** Effective constraints (global max merged with per-field constraints). */
  rules?: FileRules
  /** Served allowlist — drives the `accept` attribute and the hint text. */
  catalog?: AllowedFileType[]
  uploadLabel?: string
  /** Several files at once, uploaded one after the other. */
  multiple?: boolean
  /** Most files the field may hold in total (multiple mode); unlimited when absent. */
  maxFiles?: number
  /** Files already attached to the field; they count toward {@link maxFiles}. */
  attachedCount?: number
}

export type UploadOptions = FileUploadOptions & { onProgress: (percent: number) => void; signal: AbortSignal }

type EntryStatus = 'preparing' | 'ready' | 'uploading' | 'done' | 'failed'

interface Entry {
  id: number
  /** The file the person chose. */
  file: File
  /** What will be uploaded (optimised when that helps); set once preparation finished. */
  prepared?: PreparedFile
  status: EntryStatus
  progress: number
  error?: string
}

let nextEntryId = 1

/**
 * Reusable upload modal: file picker (click or drag & drop), progress bar, cancel/abort, and
 * inline error reporting for limit/type/server failures. In multiple mode it takes several files,
 * validates each, uploads them one at a time and keeps going when one fails.
 */
export function FileUploadModal({
  open,
  onOpenChange,
  upload,
  onUploaded,
  title = 'Upload document',
  description,
  rules = {},
  catalog = [],
  uploadLabel,
  multiple = false,
  maxFiles,
  attachedCount = 0,
}: FileUploadModalProps) {
  const [entries, setEntries] = useState<Entry[]>([])
  const [notices, setNotices] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const uploadedRef = useRef<UploadedFile[]>([])

  // Reset state each time the modal opens; abort any in-flight upload on unmount.
  useEffect(() => {
    if (open) {
      setEntries([])
      setNotices([])
      setUploading(false)
      setDragging(false)
      uploadedRef.current = []
    }
  }, [open])

  useEffect(() => () => abortRef.current?.abort(), [])

  /** How many more files this modal may take, counting the field's attached files and the ones listed here. */
  const capacityFor = useCallback(
    (listed: number) => (multiple && maxFiles != null ? Math.max(0, maxFiles - attachedCount - listed) : Infinity),
    [multiple, maxFiles, attachedCount]
  )

  const patchEntry = (id: number, patch: Partial<Entry>) =>
    setEntries((current) => current.map((e) => (e.id === id ? { ...e, ...patch } : e)))

  /** Optimises the file, then checks its size as it will be uploaded (a 12 MB photo may fit a 10 MB limit). */
  const prepareEntry = useCallback(async (entry: Entry) => {
    const prepared = await prepareFileForUpload(entry.file, rules)
    const problem = validatePrepared(entry.file, prepared, rules)
    if (problem) {
      setEntries((current) => current.filter((e) => e.id !== entry.id))
      setNotices((current) => [...current, multiple ? `${entry.file.name}: ${problem}` : problem])
      return
    }
    patchEntry(entry.id, { status: 'ready', prepared })
  }, [rules, multiple])

  const pickFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return
      const problems: string[] = []
      const accepted: File[] = []
      for (const file of files) {
        // The type is checked on what was chosen; the size after optimising
        const message = validatePrepared(file, { file, originalSize: file.size },
          { allowedTypes: rules.allowedTypes })
        if (message) problems.push(multiple ? `${file.name}: ${message}` : message)
        else accepted.push(file)
      }

      let taken = accepted
      if (multiple) {
        const room = capacityFor(entries.filter((e) => e.status !== 'done').length)
        taken = accepted.slice(0, room)
        if (accepted.length > room) {
          problems.push(room === 0
            ? `This field already holds the most files it allows (${maxFiles}).`
            : `Only ${room} more file${room === 1 ? '' : 's'} can be added (the field allows ${maxFiles}).`)
        }
      } else {
        taken = accepted.slice(0, 1)
      }
      setNotices(problems)
      const created: Entry[] = taken.map((file) => ({ id: nextEntryId++, file, status: 'preparing', progress: 0 }))
      // One file: a new pick replaces the last one
      setEntries((current) => (multiple ? [...current, ...created] : created))
      created.forEach((entry) => void prepareEntry(entry))
    },
    [rules, multiple, capacityFor, maxFiles, entries, prepareEntry]
  )

  const startUpload = async () => {
    const queue = entries.filter((e) => e.status === 'ready' || e.status === 'failed')
    if (queue.length === 0) return
    const controller = new AbortController()
    abortRef.current = controller
    setUploading(true)
    setNotices([])

    let finished = 0
    let failed = 0
    for (const entry of queue) {
      if (controller.signal.aborted) break
      patchEntry(entry.id, { status: 'uploading', progress: 0, error: undefined })
      try {
        const prepared = entry.prepared ?? { file: entry.file, originalSize: entry.file.size }
        const uploaded = await upload(prepared.file, {
          onProgress: (percent) => patchEntry(entry.id, { progress: percent }),
          signal: controller.signal,
          contentEncoding: prepared.contentEncoding,
          originalSize: prepared.contentEncoding ? prepared.originalSize : undefined,
        })
        if (uploaded) uploadedRef.current.push(uploaded)
        finished++
        patchEntry(entry.id, { status: 'done', progress: 100 })
      } catch (err) {
        if (controller.signal.aborted) {
          patchEntry(entry.id, { status: 'ready', progress: 0 })
          setNotices(['Upload cancelled'])
          break
        }
        failed++
        patchEntry(entry.id, { status: 'failed', error: getErrorMessage(err, 'Upload failed. Please try again.') })
      }
    }

    setUploading(false)
    abortRef.current = null
    if (finished > 0) {
      onUploaded?.(uploadedRef.current)
      uploadedRef.current = []
    }
    // Close when everything went through; otherwise stay open so failures can be retried or dismissed.
    if (failed === 0 && !controller.signal.aborted) onOpenChange(false)
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      abortRef.current?.abort()
    }
    onOpenChange(next)
  }

  const accept = buildAcceptAttribute(rules, catalog)
  const maxSize = rules.maxFileSizeBytes ?? null
  const waiting = entries.filter((e) => e.status !== 'done')
  const current = entries.find((e) => e.status === 'uploading')
  const canAddMore = multiple && capacityFor(waiting.length) > 0
  const finishedCount = entries.filter((e) => e.status === 'done').length
  const readyCount = entries.filter((e) => e.status === 'ready' || e.status === 'failed').length
  const preparing = entries.some((e) => e.status === 'preparing')
  const resolvedDescription = description
    ?? (multiple ? 'Choose files to upload. They are uploaded one after the other and can be cancelled at any time.'
      : 'Choose a file to upload. The upload can be cancelled at any time.')
  const resolvedLabel = uploadLabel ?? (multiple && readyCount > 1 ? `Upload ${readyCount} files` : 'Upload')

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{resolvedDescription}</DialogDescription>
        </DialogHeader>

        {/* Drop zone */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => !uploading && inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click()
          }}
          onDragOver={(event) => {
            event.preventDefault()
            if (!uploading) setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            if (!uploading) pickFiles(Array.from(event.dataTransfer.files ?? []))
          }}
          className={cn(
            'flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors',
            uploading ? 'cursor-default opacity-70' : 'cursor-pointer hover:border-primary-400 hover:bg-primary-50/40',
            dragging ? 'border-primary-400 bg-primary-50/60' : 'border-neutral-200'
          )}
        >
          <UploadCloud className={cn('h-8 w-8', dragging ? 'text-primary-600' : 'text-neutral-300')} />
          <p className="text-sm font-medium text-neutral-700">
            {!multiple && entries[0]
              ? entries[0].file.name
              : multiple
                ? 'Click to choose files or drag them here'
                : 'Click to choose a file or drag it here'}
          </p>
          {!multiple && entries[0] ? (
            <p className="text-xs text-neutral-400">
              {entries[0].status === 'preparing' ? 'Optimising…' : formatBytes(entries[0].prepared?.file.size ?? entries[0].file.size)}
            </p>
          ) : (
            <p className="text-xs text-neutral-400">
              {[
                maxSize ? `Maximum size: ${formatBytes(maxSize)}` : 'Any file type',
                multiple && maxFiles != null
                  ? `${Math.max(0, maxFiles - attachedCount)} more file${maxFiles - attachedCount === 1 ? '' : 's'} allowed`
                  : null,
              ].filter(Boolean).join(' · ')}
            </p>
          )}
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept={accept}
            multiple={multiple}
            disabled={uploading || (multiple && !canAddMore)}
            onChange={(event) => {
              pickFiles(Array.from(event.target.files ?? []))
              event.target.value = ''
            }}
          />
        </div>

        {/* Selected files + per-file status */}
        {entries.length > 0 && (
          <ul className="max-h-56 space-y-1.5 overflow-y-auto" aria-label="Selected files">
            {entries.map((entry) => (
              <li key={entry.id} className="rounded-lg border border-neutral-100 bg-neutral-50 px-3 py-2">
                <div className="flex items-center gap-2">
                  {entry.status === 'done'
                    ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                    : entry.status === 'failed'
                      ? <AlertCircle className="h-4 w-4 shrink-0 text-destructive-500" />
                      : <FileText className="h-4 w-4 shrink-0 text-neutral-400" />}
                  <span className="min-w-0 flex-1 truncate text-sm text-neutral-700">{entry.file.name}</span>
                  <span className="shrink-0 text-xs text-neutral-400">
                    {entry.status === 'preparing' ? 'Optimising…' : formatBytes((entry.prepared?.file.size ?? entry.file.size))}
                  </span>
                  {!uploading && entry.status !== 'done' && (
                    <button
                      type="button"
                      onClick={() => setEntries((all) => all.filter((e) => e.id !== entry.id))}
                      className="text-neutral-400 transition-colors hover:text-neutral-600"
                      aria-label={`Remove ${entry.file.name}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {entry.status === 'uploading' && (
                  <div className="mt-1.5 space-y-1">
                    <Progress value={entry.progress} className="h-1.5" />
                    <p className="text-right text-[11px] text-neutral-400">{entry.progress}%</p>
                  </div>
                )}
                {entry.prepared && entry.status !== 'done' && describeSavings(entry.file.size, entry.prepared.file.size) && (
                  <p className="mt-1 text-xs text-emerald-600">{describeSavings(entry.file.size, entry.prepared.file.size)}</p>
                )}
                {entry.error && <p className="mt-1 text-xs text-destructive-700">{entry.error}</p>}
              </li>
            ))}
          </ul>
        )}

        {notices.length > 0 && (
          <div className="space-y-1 rounded-lg border border-destructive-200 bg-destructive-50 px-3 py-2" role="alert">
            {notices.map((notice) => (
              <p key={notice} className="flex items-start gap-2 text-sm text-destructive-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive-500" />
                {notice}
              </p>
            ))}
          </div>
        )}

        <DialogFooter className="border-t pt-3">
          <Button
            variant="outline"
            onClick={() => {
              if (uploading) {
                abortRef.current?.abort()
                return
              }
              handleOpenChange(false)
            }}
          >
            {uploading ? 'Cancel upload' : finishedCount > 0 ? 'Close' : 'Cancel'}
          </Button>
          <Button onClick={startUpload} disabled={readyCount === 0 || uploading || preparing}>
            {uploading ? `Uploading… ${current?.progress ?? 0}%` : resolvedLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
